import { useEffect, useRef, useState } from "react";
import { t } from "@connect/i18n";
import { track } from "@connect/observability";
import type { CardBrand, CardFieldsHandle } from "./components/card-fields";
import type { StructuredAddress } from "@connect/component-library/components/address-autocomplete";
import { isValidPostCode } from "./validate-postcode";
import { resolveCardError } from "./card-error";

type SubmitFn = (args: {
  submit: () => Promise<{
    tokenId: string;
    brand: CardBrand;
    last4: string;
  }>;
}) => Promise<void>;

// Card-form submit state machine, lifted so the card fields and the button
// footer can render in separate animated regions while sharing state.
export function useCardForm(
  address: StructuredAddress | null,
  onSubmit: SubmitFn,
  binBusy: boolean,
) {
  const [cardValid, setCardValid] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [succeeded, setSucceeded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // "zip" = billing-address guard (survives card edits); "submit" = decline /
  // transport error (cleared once the user starts fixing the card).
  const [errorKind, setErrorKind] = useState<"submit" | "zip" | null>(null);
  const [submitShake, setSubmitShake] = useState(0);
  const cardFieldsRef = useRef<CardFieldsHandle>(null);

  // binBusy covers both a disallowed BIN and a still-in-flight check, so a fast
  // submit can't beat the allow-list lookup.
  const ready = cardValid && address !== null && !submitting && !binBusy;

  // Clear a stale submit/decline error when the user edits the card so it can't
  // linger and mask the field-level hint. The ZIP guard is left intact — it's
  // about the billing address, which card edits don't touch.
  const handleCardValidChange = (valid: boolean) => {
    setCardValid(valid);
    if (errorKind === "submit") {
      setError(null);
      setErrorKind(null);
    }
  };

  const handleSubmit = async () => {
    // The button is disabled until the form is submittable, so a non-ready
    // call is a no-op — no guidance shake, the button simply does nothing.
    if (!ready || succeeded) {
      return;
    }
    const handle = cardFieldsRef.current;
    if (!handle || !address) {
      return;
    }
    // Intent, fired before the zip guard so the clicked -> submitted funnel
    // exposes billing-zip drop-off.
    track.info("connect.add_card.clicked");
    // The autocomplete path commits a Google Places address directly, so its
    // ZIP is never seen by the manual form's blur validation. Guard it here.
    if (!isValidPostCode(address.country?.alpha3, address.postCode)) {
      setError(t("addCard.errors.billingPostCode"));
      setErrorKind("zip");
      setSubmitShake((n) => n + 1);
      return;
    }
    setError(null);
    setErrorKind(null);
    setSubmitting(true);
    const billingAddress = {
      street: address.street,
      subStreet: address.subStreet,
      town: address.town,
      postCode: address.postCode,
      state: address.state?.code ?? null,
      country: address.country?.alpha3 ?? "",
    };
    try {
      track.info("connect.add_card.submitted");
      await onSubmit({ submit: () => handle.submit(billingAddress) });
      setSucceeded(true);
      track.info("connect.add_card.succeeded");
      setSubmitting(false);
    } catch (err) {
      const resolved = resolveCardError(err);
      track.info("connect.add_card.failed", { reason: resolved.reason });
      setError(resolved.message);
      setErrorKind("submit");
      setSubmitShake((n) => n + 1);
      setSubmitting(false);
    }
  };

  // Return key submits when the form is valid; otherwise it's ignored.
  const readyRef = useRef(ready);
  const handleSubmitRef = useRef(handleSubmit);
  useEffect(() => {
    readyRef.current = ready;
    handleSubmitRef.current = handleSubmit;
  });
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Enter" || !readyRef.current) {
        return;
      }
      e.preventDefault();
      void handleSubmitRef.current();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  return {
    cardValid,
    setCardValid: handleCardValidChange,
    submitting,
    succeeded,
    error,
    submitShake,
    cardFieldsRef,
    ready,
    handleSubmit,
  };
}
